"use client"

import { useState, useEffect, useContext, useCallback } from "react"
import type { ReactNode } from "react"
import { AuthContext } from "../../Auth/context/AuthContext" // Real AuthContext
import {
  Plus,
  Upload,
  Search,
  Filter,
  RefreshCw,
  AlertCircle,
  Download,
  FileSpreadsheet,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Users,
  Handshake,
  Truck,
  Hammer,
  UserCog,
  HardHat,
  MoreHorizontal,
  Building,
  User,
} from "lucide-react"
import ContactsTable from "../components/ContactsTable"
import ContactForm from "../components/ContactForm"
import ContactImportWizard from "../wizard/ContactImportWizard"
import type { Contact, FilterOptions } from "../types"
import axios from "axios"
import PillFilter from "../components/ui/PillFilter"
import useContactTypes from "../hooks/useContactTypes"
import downloadBlob from "../utils/downloadBlob"

export default function ContactsCRMPage() {
  const { auth } = useContext(AuthContext)
  const [contacts, setContacts] = useState<Contact[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [showImportWizard, setShowImportWizard] = useState(false)
  const [showContactForm, setShowContactForm] = useState(false)
  const [editingContact, setEditingContact] = useState<Contact | null>(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [searchTerm, setSearchTerm] = useState("")
  const [filters, setFilters] = useState<FilterOptions>({
    contactKind: "All",
    contactType: "All",
  })
  const contactTypes = useContactTypes()
  const [exporting, setExporting] = useState(false)
  const [templating, setTemplating] = useState(false)
  const [subscription] = useState<{status: string; trialEnd: string}>({
    status: "active", // or "inactive", "trial"
    trialEnd: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days from now
  })

  const isSubscriptionInactive = subscription?.status === "inactive"
  // --- Pill filter definitions ---
  const KIND_PILLS = [
    { value: 'All',        label: 'All',        icon: <Users className="h-4 w-4" /> },
    { value: 'Business',   label: 'Business',   icon: <Building className="h-4 w-4" /> },
    { value: 'Individual', label: 'Individual', icon: <User className="h-4 w-4" /> },
  ]
  const ROLE_ICON: Record<string, ReactNode> = {
    Client:        <Handshake   className="h-4 w-4" />,  
    Supplier:      <Truck       className="h-4 w-4" />,  
    Subcontractor: <Hammer      className="h-4 w-4" />,  
    Staff:         <UserCog     className="h-4 w-4" />,  
    Ironworker:    <HardHat     className="h-4 w-4" />,  
    Partner:       <Users       className="h-4 w-4" />,  
    Misc:          <MoreHorizontal className="h-4 w-4" />,  
  }

  // Load contact types & contacts from API
  const loadContacts = useCallback(async () => {
    setLoading(true)
    try {
      // load contacts with filters
      const params: {
        page: number;
        limit: number;
        search?: string;
        contactKind?: FilterOptions['contactKind'];
        contactType?: FilterOptions['contactType'];
      } = { page, limit: 5 };
      if (searchTerm) params.search = searchTerm;
      if (filters.contactKind !== 'All') params.contactKind = filters.contactKind;
      if (filters.contactType !== 'All') params.contactType = filters.contactType;
      const resp = await axios.get(
        "/api/myteconstruction/contacts",
        { headers: { Authorization: `Bearer ${auth.token}` }, params }
      );
      setContacts(resp.data.data);
      setTotalPages(resp.data.totalPages || 1);
      setError('');
    } catch (err) {
      console.error('Error loading contacts:', err);
      setError('Failed to load contacts.');
    } finally {
      setLoading(false);
    }
  }, [auth?.token, page, searchTerm, filters]);
  useEffect(() => {
    if (auth?.token) loadContacts()
  }, [auth?.token, page, searchTerm, filters, loadContacts])

  const handleCreateContact = () => {
    setEditingContact(null)
    setShowContactForm(true)
  }

  const handleEditContact = (contact: Contact) => {
    setEditingContact(contact)
    setShowContactForm(true)
  }

  const handleDeleteContact = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this contact?")) return
    setLoading(true)
    try {
      await axios.delete(
        `/api/myteconstruction/contacts/${id}`,
        { headers: { Authorization: `Bearer ${auth.token}` } }
      )
      await loadContacts()
    } catch (err) {
      console.error("Error deleting contact:", err)
    } finally {
      setLoading(false)
    }
  }

  const handleContactFormSubmit = () => {
    setShowContactForm(false)
    // Refresh contacts after save
    loadContacts()
  }

  const handleImportSuccess = () => {
  setShowImportWizard(false)
  // Refresh contacts after import
  loadContacts()
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    setPage(1) // Reset to first page on new search
  }

  const handleFilterChange = (newFilters: Partial<FilterOptions>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }))
    setPage(1) // Reset to first page on filter change
  }

  const handleExportContacts = async () => {
    if (exporting) return
    setExporting(true)
    try {
      const resp = await axios.get(
        "/api/myteconstruction/contacts/export-excel",
        { headers: { Authorization: `Bearer ${auth.token}` }, responseType: 'blob' }
      )
      downloadBlob(resp.data, "contacts.xlsx")
    } catch (err) {
      alert("Export failed")
      console.error(err)
    } finally {
      setExporting(false)
    }
  }
  const handleDownloadTemplate = async () => {
    if (templating) return
    setTemplating(true)
    try {
      const resp = await axios.get(
        "/api/myteconstruction/contacts/template",
        { headers: { Authorization: `Bearer ${auth.token}` }, responseType: 'blob' }
      )
      downloadBlob(resp.data, "contacts-template.xlsx")
    } catch (err) {
      alert("Template download failed")
      console.error(err)
    } finally {
      setTemplating(false)
    }
  }

  if (!auth?.token) {
    return (
      <div className="min-h-screen bg-gray-950 text-white p-4 flex items-center justify-center">
        <div className="bg-gray-900 p-6 rounded-lg shadow-lg border border-gray-800 max-w-md w-full text-center">
          <AlertCircle className="h-12 w-12 mx-auto mb-4 text-yellow-500" />
          <p className="text-lg font-medium">Please log in to view Contacts.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gray-950 text-white min-h-screen">
      {/* Top Bar */}
      <div className="flex flex-col md:flex-row items-center justify-between bg-gray-900 p-3 border-b border-gray-800 sticky top-0 z-10 shadow-md">
        {isSubscriptionInactive && (
          <div className="bg-red-600 text-white px-3 py-1 rounded-md shadow-md text-sm mb-2 md:mb-0 mr-4 flex items-center gap-1.5">
            <AlertCircle className="h-4 w-4" />
            Your subscription is expired/inactive.{" "}
            <a href="/myteconstruction/upgrade" className="underline hover:text-white ml-1">
              Upgrade
            </a>
          </div>
        )}
        <div className="w-full md:w-auto mb-2 md:mb-0 flex items-center gap-2">
          <div className="relative flex-1 md:w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              className="bg-gray-800 text-white rounded-lg pl-10 pr-4 py-2 w-full focus:ring-2 focus:ring-blue-500 focus:outline-none"
              placeholder="Search contacts..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
          <button
            className="bg-gray-800 p-2 rounded-lg hover:bg-gray-700 transition-colors"
            title="Filter contacts"
            onClick={() => {
              /* Open filter modal/dropdown */
            }}
          >
            <Filter className="h-4 w-4 text-gray-300" />
          </button>
          <button
            className="bg-gray-800 p-2 rounded-lg hover:bg-gray-700 transition-colors"
            title="Refresh data"
            onClick={() => loadContacts()}
          >
            <RefreshCw className="h-4 w-4 text-gray-300" />
          </button>
        </div>
        <div className="flex space-x-2">
          <button
            className="bg-blue-600 px-3 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 shadow-md disabled:opacity-50 text-sm font-medium"
            onClick={handleCreateContact}
            disabled={isSubscriptionInactive}
          >
            <Plus className="h-4 w-4" />
            <span>New Contact</span>
          </button>
          <button
            className="bg-purple-600 px-3 py-2 rounded-lg hover:bg-purple-700 flex items-center gap-2 shadow-md disabled:opacity-50 text-sm font-medium"
            onClick={() => setShowImportWizard(true)}
            disabled={isSubscriptionInactive}
          >
            <Upload className="h-4 w-4" />
            <span>Import Contacts</span>
          </button>
          <button
            className="bg-green-600 px-3 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2 shadow-md disabled:opacity-50 text-sm font-medium"
            onClick={handleExportContacts}
            disabled={!contacts.length || isSubscriptionInactive || exporting}
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            <span className="hidden sm:inline">Export</span>
          </button>
          <button
            className="bg-purple-600 px-3 py-2 rounded-lg hover:bg-purple-700 flex items-center gap-2 shadow-md disabled:opacity-50 text-sm font-medium"
            onClick={handleDownloadTemplate}
            disabled={isSubscriptionInactive || templating}
          >
            {templating ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
            <span className="hidden sm:inline">Download Template</span>
          </button>
        </div>
      </div>

      {/* Pill Filter Bar */}
      <div className="bg-gray-900 p-3 border-b border-gray-800 space-y-3 md:space-y-0 md:flex md:items-center md:gap-6">
        {/* Contact Kind Pills */}
        <div className="flex flex-wrap gap-2">
          {KIND_PILLS.map((pill) => (
            <PillFilter
              key={pill.value}
              label={pill.label}
              icon={pill.icon}
              active={filters.contactKind === pill.value}
              onClick={() =>
                handleFilterChange({ contactKind: pill.value as FilterOptions['contactKind'] })
              }
            />
          ))}
        </div>
        {/* Sub-Type Pills */}
        <div className="flex flex-wrap gap-2 overflow-x-auto py-1">
          {["All", ...contactTypes.filter(t => t !== 'Individual')].map((type) => (
            <PillFilter
              key={type}
              label={type}
              icon={ROLE_ICON[type] ?? null}
              active={filters.contactType === type}
              onClick={() => handleFilterChange({ contactType: type })}
            />
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="p-4">
        {error && (
          <div className="mb-4 bg-red-900/30 border border-red-700 p-3 rounded-lg text-sm flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-red-400" />
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        ) : contacts.length > 0 ? (
          <>
            <ContactsTable contacts={contacts} onEdit={handleEditContact} onDelete={handleDeleteContact} />

            {/* Pagination */}
            <div className="flex items-center justify-center mt-6 space-x-4">
              <button
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                disabled={page <= 1}
                className="bg-gray-800 px-3 py-2 rounded-lg disabled:opacity-50 hover:bg-gray-700 flex items-center gap-1 transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
                <span>Prev</span>
              </button>
              <span className="text-gray-300 bg-gray-800 px-4 py-2 rounded-lg">
                Page <span className="font-medium">{page}</span> of <span className="font-medium">{totalPages}</span>
              </span>
              <button
                onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={page >= totalPages}
                className="bg-gray-800 px-3 py-2 rounded-lg disabled:opacity-50 hover:bg-gray-700 flex items-center gap-1 transition-colors"
              >
                <span>Next</span>
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </>
        ) : (
          <div className="bg-gray-900 p-6 rounded-lg shadow-lg border border-gray-800 text-center">
            <div className="flex flex-col items-center justify-center h-64">
              <div className="text-center">
                <Users className="w-16 h-16 text-blue-500 mx-auto mb-4 opacity-50" />
                <h2 className="text-xl font-medium text-gray-300 mb-2">No Contacts Found</h2>
                <p className="text-gray-500 max-w-md mb-4">
                  {searchTerm || filters.contactKind !== "All" || filters.contactType !== "All"
                    ? "No contacts match your search criteria. Try adjusting your filters."
                    : "Start adding contacts to your organization to see them listed here."}
                </p>
                <button
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-200 flex items-center gap-2 mx-auto"
                  onClick={handleCreateContact}
                  disabled={isSubscriptionInactive}
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Contact</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black bg-opacity-75 overflow-y-auto flex items-start sm:items-center justify-center z-50 py-4">
          <div
            className="bg-gray-900 p-6 rounded-lg max-w-2xl w-full border border-gray-700 shadow-xl max-h-[90vh] overflow-y-auto"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <ContactForm
              contact={editingContact}
              onClose={() => setShowContactForm(false)}
              onSubmit={handleContactFormSubmit}
            />
          </div>
        </div>
      )}

      {/* Import Wizard Modal */}
      {showImportWizard && (
        <div className="fixed inset-0 bg-black bg-opacity-75 overflow-y-auto flex items-start sm:items-center justify-center z-50 py-4">
          <div
            className="bg-gray-900 p-6 rounded-lg max-w-4xl w-full border border-gray-700 shadow-xl max-h-[90vh] overflow-y-auto"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <ContactImportWizard onClose={() => setShowImportWizard(false)} onSuccess={handleImportSuccess} />
          </div>
        </div>
      )}
    </div>
  )
}
